import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityInboxType,
  ActivityOrderBy,
  FeatureName,
  getActivityFilter,
  SortDirectionEnum,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useFindUnreadActivityInbox,
  useIsFeatureEnabled,
  useSetActivityListFilter
} from '@gripp/shared-logic';
import { createColumnHelper } from '@tanstack/react-table';
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleSheet,
  Text,
  View
} from 'react-native';
import { useParams } from 'react-router-dom';
import { Colors } from '../../themes/colors';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { ActivityListOld } from './activityList-old';
import { ActivitySortDropdown } from './activitySortDropdown';
import { IssueCard, IssueCardData } from './issueCard';
import { IssueCardCompact } from './issueCardCompact';

export type ActivityListProps = {
  assetId?: string;
  showAssetName?: boolean;
  onIssueSelected?: (id: string) => void;
  onActivityCountChange?: (count: number) => void;
  selectedIssue?: string;
  listMaxHeight?: number;
  showScrollIndicator?: boolean;
  containerStyle?: React.ComponentProps<typeof View>['style'];
  right?: React.ComponentType;
  filterKey: ActivityFilterKey;
  showTextEmpty?: boolean;
  excludeFilter?: boolean;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
  /**
   * Which card to render inside the list.
   * - default: full IssueCard (existing behavior)
   * - assetCompact: compact card for asset preview (title+icons+date+description)
   */
  cardVariant?: 'default' | 'assetCompact';
  /**
   * Page size used by the infinite grid query.
   */
  pageSize?: number;
  /**
   * Whether to show the list top bar (results + sort).
   */
  showTopBar?: boolean;
};

const getOrderByField = (orderBy: ActivityOrderBy): string => {
  if (orderBy === ActivityOrderBy.MostRecentUpdated) {
    return 'updatedAt';
  }
  return orderBy;
};


// TODO: Crop Circle Redesign | remove once feature is fully rolled out
export const ActivityListBase = (props: ActivityListProps) => {
  const { data: filterData, isLoading: isLoadingFilter } =
    useActivityListFilter(props.filterKey);
  const { account } = useCurrentAccount();

  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(props.filterKey);

  const { t } = useTranslation();
  const [filter, setFilter] = useState(undefined);
  const [inbox, setInbox] = useState<any[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const { issueId } = useParams();
  const swipeableRegistryRef = useRef<Map<string, React.RefObject<any>>>(new Map());
  const activeSwipeableIdRef = useRef<string | null>(null);
  const lastNavigationRef = useRef(0);

  const { data: inboxData, isLoading: isLoadingInbox } =
    useFindUnreadActivityInbox();

  const workspace = useCurrentWorkspace(true);

  const currentOrderBy = filterData?.orderBy || ActivityOrderBy.MostRecentUpdated;
  const orderByField = getOrderByField(currentOrderBy);

  useEffect(() => {
    if (inboxData) {
      setInbox(inboxData.items);
    }
  }, [inboxData]);

  useEffect(() => {
    if (!filterData || workspace === undefined) return;

    // reset filtering only for Recent Activity
    const aFilter = props.excludeFilter
      ? new ActivityFilterModel()
      : filterData;

    const f = getActivityFilter(aFilter, workspace?.id, props.assetId);

    setFilter(f);
  }, [filterData, workspace, props.assetId]);

  const hasUnread = useCallback((activity: any) => {
    return inbox.some(
      (i) =>
        (
          i.type === ActivityInboxType.IssueUpdated ||
          i.type === ActivityInboxType.IssueMessageCreated
        ) &&
        i.data.id === activity.id
    );
  }, [inbox]);

  const isMentionedInInbox = useCallback((activity: any) => {
    const inboxItem = inbox.find(
      (i) =>
        i.type === ActivityInboxType.IssueMessageCreated &&
        i.data.id === activity.id
    );
    return inboxItem?.data?.isMentioned || false;
  }, [inbox]);

  const rowStyle = (row: any) => {
    const isUnread = hasUnread(row.original);
    const isMentioned = isMentionedInInbox(row.original);

    if (props.cardVariant === 'assetCompact') {
      return styles.compactRow;
    }

    return [
      styles.row,
      (isUnread && isMentioned) ? styles.unread : styles.read,
    ];
  };

  const columnHelper = createColumnHelper<any>();

  const registerSwipeable = useCallback((id: string, ref: React.RefObject<any>) => {
    swipeableRegistryRef.current.set(id, ref);
  }, []);

  const unregisterSwipeable = useCallback((id: string) => {
    swipeableRegistryRef.current.delete(id);
  }, []);

  const closeOtherSwipeables = useCallback((currentId: string) => {
    const previousId = activeSwipeableIdRef.current;
    if (previousId && previousId !== currentId) {
      swipeableRegistryRef.current.get(previousId)?.current?.close?.();
    }
    activeSwipeableIdRef.current = currentId;
  }, []);

  const handleSwipeClose = useCallback((id: string) => {
    if (activeSwipeableIdRef.current === id) {
      activeSwipeableIdRef.current = null;
    }
  }, []);

  const columns: any[] = [
    columnHelper.accessor('id', {
      cell: ({ row }) => {
        const activity = row.original as IssueCardData;
        return (
          activity && (
            props.cardVariant === 'assetCompact' ? (
              <IssueCardCompact
                account={account}
                activity={activity}
                showAssetName={props.showAssetName}
                isUnread={hasUnread(activity)}
                isMentionedInInbox={isMentionedInInbox(activity)}
                filter={filterData}
                isSelected={activity.id === (props.selectedIssue || issueId)}
                isCropCircleRedesignEnabled={true}
              />
            ) : (
              <IssueCard
                account={account}
                activity={activity}
                showAssetName={props.showAssetName}
                isUnread={hasUnread(activity)}
                isMentionedInInbox={isMentionedInInbox(activity)}
                filter={filterData}
                isSelected={activity.id === (props.selectedIssue || issueId)}
                isCropCircleRedesignEnabled={true}
                onRegisterSwipeable={registerSwipeable}
                onUnregisterSwipeable={unregisterSwipeable}
                onSwipeOpen={closeOtherSwipeables}
                onSwipeClose={handleSwipeClose}
              />
            )
          )
        );
      },
      meta: {
        style: styles.cell,
        customRender: true,
      },
    }),
  ];

  if (props.right) {
    const RightComponent = props.right;
    columns.push(
      columnHelper.accessor('type', {
        cell: ({ row }) => {
          const activity = row.original as IssueCardData;
          return activity && (RightComponent as any)({ activity });
        },
        meta: {
          style: styles.right,
          customRender: true,
        },
      })
    );
  }

  const onActivitySelected = (activity: any) => {
    const now = Date.now();
    if (now - lastNavigationRef.current < 500) return;
    lastNavigationRef.current = now;
    props.onIssueSelected && props.onIssueSelected(activity.id);
  };

  const handleSortChange = async (newSort: ActivityOrderBy) => {
    if (filterData) {
      await setActivityListFilter(
        new ActivityFilterModel({
          ...filterData,
          orderBy: newSort,
        })
      );
    }
  };

  const onDataLoaded = (data: any[]) => {
    props.onDataLoaded?.(data);
  };

  if (isLoadingFilter || isLoadingInbox) {
    return null;
  }

  return !filter ? null : (
    <View
      style={[
        styles.container,
        props.cardVariant === 'assetCompact' && styles.compactContainer,
      ]}
    >
      {props.showTopBar !== false && (
      <View
        style={
          Platform.OS === 'web'
            ? styles.desktopStickyHeader
            : styles.mobileStickyHeader
        }
      >
        <Text style={styles.resultsText}>
          {itemsCount} {t('activity.topBar.results')}
        </Text>
        <ActivitySortDropdown
          selectedSort={currentOrderBy}
          onSortChange={handleSortChange}
        />
      </View>
      )}
      <InfiniteDataGrid
        columns={columns}
        query={getActivities}
        dataPrefix="issue.findIssues"
        modelName="issue"
        onRowClick={onActivitySelected}
        orderBy={{
          field: orderByField,
          order: SortDirectionEnum.Desc,
        }}
        pageSize={props.pageSize ?? 50}
        filter={filter}
        hideHeader={true}
        hideResultsLabel={true}
        refetchOnFocus={true}
        listMaxHeight={props.listMaxHeight as number}
        showScrollIndicator={props.showScrollIndicator}
        containerStyle={[
          props.containerStyle,
          props.cardVariant === 'assetCompact' && {
            backgroundColor: Colors.white,
          },
          { paddingTop: props.showTopBar === false ? 0 : 50 }
        ]}
        rowStyle={rowStyle}
        textEmpty={
          props.customEmptyState ||
          (( workspace || Platform.OS === 'web' || props.showTextEmpty) && t('activity.textEmpty'))
        }
        onItemsCountChange={(count) => {
          setItemsCount(count);
          props.onActivityCountChange?.(count);
        }}
        enableScrollRefresh={true}
        onDataLoaded={onDataLoaded}
        disableHorizontalScroll={Platform.OS !== 'web'}
      />
    </View>
  );
};

export const ActivityList = (props: ActivityListProps) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);
  
  if (!isCropCircleEnabled) {
    return <ActivityListOld {...props} />;
  }

  return <ActivityListBase {...props} />;
};  

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  compactContainer: {
    // No background color - let parent handle it
  },
  desktopStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  mobileStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.grayText,
    letterSpacing: -0.25,
  },
  cell: {
    alignSelf: 'stretch',
    alignContent: 'stretch',
    width: '100%',
  },
  row: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottomWidth: 1,
    borderBottomColor: Colors.secondaryGrayHeader,
    height: 'auto',
    minHeight: 110,
  },
  compactRow: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 2,
    paddingBottom: 2,
    borderBottomWidth: 0,
    height: 'auto',
    minHeight: 0,
    backgroundColor: Colors.white,
  },
  right: {
    maxWidth: 5,
  },
  read: {
    backgroundColor: Colors.white,
  },
  unread: {
    backgroundColor: Colors.unreadBackground,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
  },
});

const getActivities = `
    query FindIssues($filter: FindIssuesFilter, $page: PageRequest, $orderBy: OrderByInput) {
      issue {
        findIssues(filter: $filter, page: $page, orderBy: $orderBy) {
          count
          offset
          limit
          items {
            id
            severity
            type
            title
            subtitle
            isPinned
            asset {
              name
              coverImage {
                id
                url
                extension
                status
                type
              }
            }
            workspace {
              id
              name
            }
            labels {
              id
              name
            }
            createdAt
            effectiveCreationDate
            createdBy {
              id
              name {
                first
                last
              }
            }
            updatedAt
            updatedBy {
              id
              name {
                first
                last
              }
            }
            nextReminderDate
            userMentions {
              id
              name {
                first
                last
              }
            }
            latestMessage {
              text
              i18n
              createdBy {
                id
                name {
                  first
                  last
                }
              }
              userMentions {
                id
                name {
                  first
                  last
                }
              }
              reactions {
                id
                createdAt
                createdBy
                reactionType
              }
              type
            }
          }
        }
      }
    }
  `;